/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  EmailVerdict,
  VerificationContext,
  VerifyEmailButton,
} from "./EmailVerification";
import { describeWriteAction } from "./helpers/emailVerification";
import {
  AccountAddress,
  AccountFollowOnsModel,
  describeAddress,
  NewAddressValues,
} from "./helpers/useAccountFollowOns";
import classes from "./css/recordReview.module.css";

/*
 * The two things a chosen account can add to a company write. Both share one
 * shape - tick, then a control spanning the rest of the row - so their edges
 * line up and the tick reads as the same "include this" in both. Neither
 * writes on its own: each is an opt-in that rides the company's write.
 *
 * Both are underlined fields, like the account picker above them: they are
 * part of that one decision, and the outlined boxes they replaced read as a
 * second form while taking half as much height again.
 */

export const EmailFollowOnRow = (props: {
  email: AccountFollowOnsModel["email"];
  accountName: string;
  /** The field the email lands in, named as the record names it. */
  targetLabel: string;
  /*
   * The derived address is a guess - a format the account's other contacts
   * follow, applied to this name - which is exactly what a deliverability
   * check is for. Picking the account runs it; the button here stays for a
   * corrected address or a retry, and hides once a verdict exists.
   */
  verification: VerificationContext;
  disabled: boolean;
}) => {
  const { email, targetLabel } = props;
  const includeLabel = `Write this email to ${targetLabel} with the company`;
  return (
    <>
      <div className={classes.accountFollowOnRow}>
        <Tooltip
          title={
            email.reason ??
            describeWriteAction(
              email.typed,
              includeLabel,
              "written",
              "Correct the email, or leave it out of this write."
            )
          }
        >
          <span className={classes.accountFollowOnCheck}>
            <Checkbox
              size="small"
              checked={email.chosen}
              disabled={props.disabled || email.blocked || !email.writable}
              onChange={(event) => email.setIncluded(event.target.checked)}
              inputProps={{ "aria-label": includeLabel }}
              sx={{ p: 0.5, ml: -0.5 }}
            />
          </span>
        </Tooltip>
        <TextField
          variant="standard"
          size="small"
          value={email.draft}
          disabled={props.disabled}
          onChange={(event) => email.setDraft(event.target.value)}
          error={Boolean(email.typed) && !email.valid}
          helperText={email.reason}
          slotProps={{
            htmlInput: {
              "aria-label": `Work email at ${props.accountName}`,
            },
            input: {
              endAdornment: (
                <div className={classes.accountFollowOnAdornment}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {targetLabel}
                  </Typography>
                  {email.valid && (
                    <VerifyEmailButton
                      email={email.typed}
                      context={props.verification}
                      disabled={props.disabled}
                    />
                  )}
                </div>
              ),
            },
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </div>
      {email.valid && (
        <div className={classes.accountFollowOnDetail}>
          <EmailVerdict email={email.typed} context={props.verification} />
        </div>
      )}
    </>
  );
};

/*
 * The way to an office the CRM doesn't hold yet, kept as the last option
 * whatever is typed: the steward is most likely to want it after searching
 * and finding nothing.
 */
const ADD_NEW_ADDRESS: AccountAddress = {
  address_id: "__add_new_address__",
  street: "",
  city: "",
  stateorprovince: "",
  country: "",
  zipcode: "",
};
const ADD_NEW_ADDRESS_LABEL = "+ Add new address";
const filterAddresses = createFilterOptions<AccountAddress>();

const officeLabel = (address: AccountAddress) =>
  address === ADD_NEW_ADDRESS
    ? ADD_NEW_ADDRESS_LABEL
    : describeAddress(address);

export const OfficeFollowOnRow = (props: {
  office: AccountFollowOnsModel["office"];
  accountName: string;
  disabled: boolean;
  /** Offered when the CRM can take a new address; opens the form. */
  onAddNew?: () => void;
  /** The search input, so focus can come back here when the form closes. */
  inputRef?: React.Ref<HTMLInputElement>;
}) => {
  const { office, onAddNew } = props;
  return (
    <div className={classes.accountFollowOnRow}>
      <Tooltip
        title={
          office.selected
            ? "Link this office to the contact with the company"
            : "Choose an office first."
        }
      >
        <span className={classes.accountFollowOnCheck}>
          <Checkbox
            size="small"
            checked={office.included}
            disabled={props.disabled || !office.selected}
            onChange={(event) => office.setIncluded(event.target.checked)}
            inputProps={{
              "aria-label": "Link this office with the company",
            }}
            sx={{ p: 0.5, ml: -0.5 }}
          />
        </span>
      </Tooltip>
      {/*
       * Searchable, since an account can hold many offices. Options render
       * keyed on the id: two offices can share a label.
       */}
      <Autocomplete
        size="small"
        options={
          onAddNew ? [...office.addresses, ADD_NEW_ADDRESS] : office.addresses
        }
        filterOptions={(options, state) => {
          const found = filterAddresses(
            options.filter((address) => address !== ADD_NEW_ADDRESS),
            state
          );
          return onAddNew ? [...found, ADD_NEW_ADDRESS] : found;
        }}
        value={office.selected ?? null}
        disabled={props.disabled}
        getOptionLabel={officeLabel}
        isOptionEqualToValue={(option, value) =>
          option.address_id === value.address_id
        }
        onChange={(_, address) => {
          // The form decides the office; until then the selection stands.
          if (address === ADD_NEW_ADDRESS) {
            onAddNew?.();
            return;
          }
          office.select(address?.address_id ?? "");
        }}
        renderOption={(optionProps, address) => (
          <li {...optionProps} key={address.address_id}>
            {officeLabel(address)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            inputRef={props.inputRef}
            placeholder="Select an office"
            inputProps={{
              ...params.inputProps,
              "aria-label": `Office at ${props.accountName}`,
            }}
          />
        )}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiAutocomplete-input": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      />
    </div>
  );
};

/**
 * An office the CRM doesn't hold yet, typed in place. Creating it is an
 * immediate CRM write with no undo, so it goes through a confirm; what it
 * creates is then selected like any other office and linked by the write.
 */
export const NewAddressForm = (props: {
  newAddress: AccountFollowOnsModel["newAddress"];
  accountName: string;
  crmName: string;
  /**
   * Opened from the office picker, the form sits under that field and starts
   * where it does. Opened from the no-offices line, there is no field above
   * to align to, and an indent there only read as a misalignment.
   */
  underOffice: boolean;
}) => {
  const { newAddress, accountName, crmName } = props;
  const { values, creating } = newAddress;
  const [confirming, setConfirming] = useState(false);
  const field = (
    name: keyof NewAddressValues,
    label: string,
    required: boolean
  ) => (
    <TextField
      variant="standard"
      size="small"
      label={label}
      required={required}
      // The form is opened to be typed into: the first field takes focus.
      autoFocus={name === "street"}
      value={values[name]}
      disabled={creating}
      onChange={(event) => newAddress.setValue(name, event.target.value)}
      sx={{ flex: 1, minWidth: 0 }}
    />
  );
  const typed: AccountAddress = {
    address_id: "",
    street: values.street.trim(),
    city: values.city.trim(),
    stateorprovince: values.state.trim(),
    zipcode: values.postcode.trim(),
    country: values.country.trim(),
  };
  const showType = newAddress.addressTypes.length > 0;
  return (
    <div
      className={
        props.underOffice
          ? `${classes.newAddressForm} ${classes.accountFollowOnDetail}`
          : classes.newAddressForm
      }
    >
      <div className={classes.accountFollowOnRow}>
        {field("street", "Street", true)}
      </div>
      <div className={classes.accountFollowOnRow}>
        {field("city", "City", true)}
        {field("state", "State/Province", false)}
      </div>
      <div className={classes.accountFollowOnRow}>
        {field("postcode", "Postcode", false)}
        {field("country", "Country", true)}
      </div>
      {showType && (
        <div className={classes.accountFollowOnRow}>
          <TextField
            select
            variant="standard"
            size="small"
            label="Address type"
            value={values.addressType}
            disabled={creating}
            onChange={(event) =>
              newAddress.setValue("addressType", event.target.value)
            }
            sx={{ flex: 1, minWidth: 0 }}
          >
            {newAddress.addressTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}
      {newAddress.reason && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {newAddress.reason}
        </Typography>
      )}
      {newAddress.error && (
        <Typography variant="body2" role="alert" sx={{ color: "error.main" }}>
          {newAddress.error}
        </Typography>
      )}
      {/*
       * The form's own ending, laid out as the panel's is - way out, then
       * commit, at the right edge - since while it is open it is the only
       * thing that can finish. "Discard address", not "Dismiss": the panel's
       * Cancel below drops the whole edit, and this drops only the address.
       */}
      <div className={classes.newAddressActions}>
        <Button size="small" disabled={creating} onClick={newAddress.dismiss}>
          Discard address
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={creating || !newAddress.valid}
          onClick={() => setConfirming(true)}
        >
          {creating ? "Creating…" : `Create in ${crmName}`}
        </Button>
      </div>
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>{`Create this address in ${crmName}?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{accountName}</Typography>
          <Typography variant="body2">{describeAddress(typed)}</Typography>
          {showType && values.addressType && (
            <Typography variant="body2">{`Type: ${values.addressType}`}</Typography>
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            {`This creates the address on ${accountName} in ${crmName} right away. It isn't undone if you cancel the company change.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Go back</Button>
          <Button
            variant="outlined"
            onClick={() => {
              setConfirming(false);
              newAddress.create();
            }}
          >
            Create address
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
